import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useAdminAuth, type AdminStats } from "@/hooks/useAdminAuth";

interface TrendItem {
  date: string;
  newClients: number;
  activeClients: number;
}

interface FeeStats {
  totalReceivable: number;
  totalReceived: number;
  totalPending: number;
  completedCount: number;
  incompletedCount: number;
}

interface CountryFeeItem {
  country: string;
  receivable: number;
  received: number;
  pending: number;
}

interface AdvisorOption {
  id: number;
  name: string;
}

// Service plan display mapping - only Level 2 names
const SERVICE_PLAN_DISPLAY: Record<string, string> = {
  surrogacy_has_embryo: "已有胚胎",
  surrogacy_transfer_embryo: "转入胚胎",
  ivf_self: "自精自卵",
  ivf_donor: "捐赠助孕",
  embryo_transport: "胚胎转出",
  fertility_preservation: "生育力保存",
};

function formatPlanLabel(plan: string): string {
  return SERVICE_PLAN_DISPLAY[plan] || plan || "其他";
}

function CustomPieTooltip({
  active,
  payload,
  data,
  labelKey,
  valueKey,
  currency,
}: {
  active?: boolean;
  payload?: any[];
  data: any[];
  labelKey: string;
  valueKey?: string;
  currency?: string;
}) {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0].payload;
  const key = valueKey || "count";
  const total = data.reduce((sum, d) => sum + (d[key] || 0), 0);
  const percent = total > 0 ? (((item[key] || item.value || 0) / total) * 100).toFixed(1) : "0.0";
  const label = item[labelKey] || item.label || item.country || item.source || item.status || "其他";
  const val = item[key] || item.value || 0;
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 shadow-sm">
      <p className="text-xs font-medium text-[#1a3a5c] mb-0.5">{label}</p>
      <p className="text-xs text-gray-500">
        {currency}{currency ? " " : ""}{val.toLocaleString("zh-CN")}（{percent}%）
      </p>
    </div>
  );
}

function FeeTooltip({ active, payload }: { active?: boolean; payload?: any[] }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 shadow-sm">
      <p className="text-xs font-medium text-[#1a3a5c] mb-1">{payload[0].payload.country}</p>
      {payload.map((entry, idx) => (
        <p key={idx} className="text-xs text-gray-500 flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: entry.color }}></span>
          {entry.name}: {entry.name?.includes("$") ? "$" : "¥"}{Number(entry.value).toLocaleString("zh-CN")}
        </p>
      ))}
    </div>
  );
}

// Collection status tooltip for pie chart
function CollectionPieTooltip({ active, payload }: { active?: boolean; payload?: any[] }) {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0].payload;
  const total = (item.completedCount || 0) + (item.incompletedCount || 0);
  const percent = total > 0 ? ((item.count / total) * 100).toFixed(1) : "0.0";
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 shadow-sm">
      <p className="text-xs font-medium text-[#1a3a5c] mb-0.5">{item.label}</p>
      <p className="text-xs text-gray-500">{item.count} 人（{percent}%）</p>
    </div>
  );
}

function formatAmount(n: number): string {
  if (!n) return "0";
  return n.toLocaleString("zh-CN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export default function AdminOverviewPage() {
  const { fetchDashboard, isSuperAdmin, hasFinanceSubPermission, hasClientSubPermission } = useAdminAuth();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [trend, setTrend] = useState<TrendItem[]>([]);
  const [servicePlanDist, setServicePlanDist] = useState<any[]>([]);
  const [countryDist, setCountryDist] = useState<any[]>([]);
  const [sourceDist, setSourceDist] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(false);
  const [finTrendLoading, setFinTrendLoading] = useState(false);
  const [error, setError] = useState("");

  // Finance trend state
  const [financeTrend, setFinanceTrend] = useState<any[]>([]);
  const [financeTimeRange, setFinanceTimeRange] = useState("all");
  const [showFinanceTrend, setShowFinanceTrend] = useState(false);
  const [finCurrencyTab, setFinCurrencyTab] = useState<"rmb" | "usd">("rmb");

  // Fee data states
  const [feeStats, setFeeStats] = useState<FeeStats>({
    totalReceivable: 0, totalReceived: 0, totalPending: 0,
    completedCount: 0, incompletedCount: 0,
  });
  const [countryFeeData, setCountryFeeData] = useState<CountryFeeItem[]>([]);
  const [feeLoading, setFeeLoading] = useState(true);

  const [timeRange, setTimeRange] = useState("all");
  // New filters
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterAdvisor, setFilterAdvisor] = useState("all");
  const [advisorOptions, setAdvisorOptions] = useState<AdvisorOption[]>([]);
  const [usdStats, setUsdStats] = useState({ totalUsdReceivable: 0, totalUsdReceived: 0, totalUsdPending: 0 });
  const [feeTab, setFeeTab] = useState<"receivable" | "received" | "pending">("received");
  const [usdFeeTab, setUsdFeeTab] = useState<"receivable" | "received" | "pending">("received");
  const navigate = useNavigate();

  const handlePieClick = (type: "service_plan" | "country" | "client_source", value: string) => {
    const params = new URLSearchParams();
    if (type === "service_plan") params.set("service_plan", value);
    if (type === "country") params.set("country", value);
    if (type === "client_source") params.set("client_source", value);
    navigate(`/admin/clients?${params.toString()}`);
  };

  // Load global data (unaffected by timeRange)
  const loadGlobal = useCallback(async () => {
    setLoading(true);
    setFeeLoading(true);
    const res = await fetchDashboard({
      filterCountry: filterCountry !== "all" ? filterCountry : undefined,
      filterAdvisor: filterAdvisor !== "all" ? filterAdvisor : undefined,
      finance_time_range: financeTimeRange,
    } as any);
    setLoading(false);
    setFeeLoading(false);
    if (res?.success) {
      setStats(res.stats);
      setServicePlanDist(res.servicePlanDistribution || []);
      setCountryDist(res.countryDistribution || []);
      setSourceDist(res.sourceDistribution || []);
      if (res.stats) {
        setFeeStats({
          totalReceivable: res.stats.totalReceivable || 0,
          totalReceived: res.stats.totalReceived || 0,
          totalPending: res.stats.totalPending || 0,
          completedCount: res.stats.completedFeeCount || 0,
          incompletedCount: res.stats.pendingFeeCount || 0,
        });
        setUsdStats({
          totalUsdReceivable: res.stats.totalUsdReceivable || 0,
          totalUsdReceived: res.stats.totalUsdReceived || 0,
          totalUsdPending: res.stats.totalUsdPending || 0,
        });
      }
      setCountryFeeData(res.countryFeeData || []);
      if (res.finance_trend) {
        setFinanceTrend(res.finance_trend);
        setShowFinanceTrend(true);
      }
      if (res.advisors && res.advisors.length > 0) {
        setAdvisorOptions(res.advisors);
      }
    } else {
      setError(res?.error || "加载失败");
    }
  }, [fetchDashboard, filterCountry, filterAdvisor, financeTimeRange]);

  // Load trend data only (affected by timeRange)
  const loadTrend = useCallback(async () => {
    setTrendLoading(true);
    const res = await fetchDashboard({
      timeRange,
      filterCountry: filterCountry !== "all" ? filterCountry : undefined,
      filterAdvisor: filterAdvisor !== "all" ? filterAdvisor : undefined,
    } as any);
    setTrendLoading(false);
    if (res?.success) {
      setTrend(res.trend || []);
    }
  }, [fetchDashboard, timeRange, filterCountry, filterAdvisor]);

  // Initial load
  useEffect(() => {
    loadGlobal();
  }, [loadGlobal]);

  useEffect(() => {
    loadTrend();
  }, [loadTrend]);

  // Only reload trend when timeRange changes
  useEffect(() => {
    loadTrend();
  }, [timeRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-2xl text-red-400 mb-2"></i>
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  const cards = [
    { label: "总客户数", value: stats?.totalClients ?? 0, icon: "ri-user-line", color: "text-[#1a3a5c]", bg: "bg-[#1a3a5c]/5" },
    { label: "在途客户", value: stats?.activeClients ?? 0, icon: "ri-route-line", color: "text-amber-600", bg: "bg-amber-50" },
    { label: "已结束客户", value: stats?.completedClients ?? 0, icon: "ri-checkbox-circle-line", color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "应收总额（元）", value: `¥ ${formatAmount(feeStats.totalReceivable)}`, icon: "ri-money-cny-circle-line", color: "text-[#1a3a5c]", bg: "bg-[#1a3a5c]/5" },
    { label: "已收总额（元）", value: `¥ ${formatAmount(feeStats.totalReceived)}`, icon: "ri-check-double-line", color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "待收总额（元）", value: `¥ ${formatAmount(feeStats.totalPending)}`, icon: "ri-time-line", color: "text-amber-600", bg: "bg-amber-50" },
    { label: "应收总额（美元）", value: `$ ${formatAmount(usdStats.totalUsdReceivable)}`, icon: "ri-money-dollar-circle-line", color: "text-[#1a3a5c]", bg: "bg-[#1a3a5c]/5" },
    { label: "已收总额（美元）", value: `$ ${formatAmount(usdStats.totalUsdReceived)}`, icon: "ri-check-double-line", color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "待收总额（美元）", value: `$ ${formatAmount(usdStats.totalUsdPending)}`, icon: "ri-time-line", color: "text-amber-600", bg: "bg-amber-50" },
  ];

  // Collection status pie data
  const collectionPieData = [
    {
      label: "收款已完成",
      count: feeStats.completedCount,
      color: "#059669",
    },
    {
      label: "收款未完成",
      count: feeStats.incompletedCount,
      color: "#f59e0b",
    },
  ].filter((d) => d.count > 0);

  // Country fee chart data (top 6)
  const topCountryFees = countryFeeData.slice(0, 6);

  // Format service plan distribution labels with new display format
  const formattedPlanDist = servicePlanDist.map((d: any) => ({
    ...d,
    label: formatPlanLabel(d.plan),
  }));

  const formatDateLabel = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  const chartData = trend.map((t) => ({
    ...t,
    label: formatDateLabel(t.date),
  }));

  const exportTrendToCSV = () => {
    if (!trend.length) return;
    const headers = "日期,新增客户数,总客户数\n";
    const rows = trend.map((t) => `${t.date},${t.newClients},${t.activeClients}`).join("\n");
    const csv = "\ufeff" + headers + rows;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `客户趋势数据_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const isCountryFiltered = filterCountry !== "all";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-[#1a3a5c]">数据概览</h1>
        <p className="text-xs text-gray-400 mt-1">实时掌握 CRM 系统整体运营数据</p>
      </div>

      {/* Filters - country & advisor only */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl shadow-sm p-3">
        <select
          value={filterCountry}
          onChange={(e) => setFilterCountry(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer"
        >
          <option value="all">全部国家</option>
          {["日本", "泰国", "马来西亚", "格鲁吉亚", "哥伦比亚", "吉尔吉斯斯坦"].map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {(isSuperAdmin() || hasClientSubPermission("assign_advisor")) && advisorOptions.length > 0 && (
          <select
            value={filterAdvisor}
            onChange={(e) => setFilterAdvisor(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer"
          >
            <option value="all">全部顾问</option>
            {advisorOptions.map((a) => (
              <option key={a.id} value={String(a.id)}>{a.name}</option>
            ))}
          </select>
        )}
        {(filterCountry !== "all" || filterAdvisor !== "all") && (
          <button
            onClick={() => { setFilterCountry("all"); setFilterAdvisor("all"); }}
            className="px-3 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-close-line"></i>清除筛选
          </button>
        )}
      </div>

      {/* Stats Cards - AFTER filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((card) => (
          <div
            key={card.label}
            className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg ${card.bg} flex items-center justify-center`}>
                <i className={`${card.icon} ${card.color} text-lg`}></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-[#1a3a5c]">{card.value}</p>
            <p className="text-xs text-gray-400 mt-1">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Finance Trend Chart - conditional on permission */}
      {showFinanceTrend && hasFinanceSubPermission("stats") && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <i className="ri-funds-line text-emerald-600 text-lg"></i>
                <h3 className="text-sm font-semibold text-[#1a3a5c]">财务趋势</h3>
              </div>
              <div className="flex items-center gap-1">
                {[
                  { value: "all", label: "全部" },
                  { value: "90", label: "近90天" },
                  { value: "30", label: "近30天" },
                ].map((r) => (
                  <button
                    key={r.value}
                    onClick={() => setFinanceTimeRange(r.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      financeTimeRange === r.value ? "bg-emerald-600 text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setFinCurrencyTab("rmb")}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${finCurrencyTab === "rmb" ? "bg-[#1a3a5c] text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100"}`}
              >
                <i className="ri-money-cny-circle-line mr-1"></i>人民币
              </button>
              <button
                onClick={() => setFinCurrencyTab("usd")}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${finCurrencyTab === "usd" ? "bg-[#1a3a5c] text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100"}`}
              >
                <i className="ri-money-dollar-circle-line mr-1"></i>美元
              </button>
            </div>
          </div>
          {finTrendLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : financeTrend.length > 0 ? (
            <div className="w-full h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={financeTrend} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={{ stroke: "#e5e7eb" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v: number) => v >= 10000 ? `${(v / 10000).toFixed(1)}万` : String(v)}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e5e7eb",
                      fontSize: 12,
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    }}
                    labelStyle={{ color: "#1a3a5c", fontWeight: 600, marginBottom: 4 }}
                    formatter={(value: any, name: string) => {
                      const prefix = finCurrencyTab === "usd" ? "$" : "¥";
                      if (name === "income" || name === "income_usd") return [`${prefix}${Number(value).toLocaleString("zh-CN")}`, "收入"];
                      if (name === "expense" || name === "expense_usd") return [`${prefix}${Number(value).toLocaleString("zh-CN")}`, "支出"];
                      if (name === "profit" || name === "profit_usd") return [`${prefix}${Number(value).toLocaleString("zh-CN")}`, "利润"];
                      return [value, name];
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                    formatter={(value: string) =>
                      value === "income" || value === "income_usd" ? "收入" : value === "expense" || value === "expense_usd" ? "支出" : "利润"
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey={finCurrencyTab === "usd" ? "income_usd" : "income"}
                    stroke="#059669"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#059669", strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
                    name={finCurrencyTab === "usd" ? "income_usd" : "income"}
                  />
                  <Line
                    type="monotone"
                    dataKey={finCurrencyTab === "usd" ? "expense_usd" : "expense"}
                    stroke="#dc2626"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#dc2626", strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
                    name={finCurrencyTab === "usd" ? "expense_usd" : "expense"}
                  />
                  <Line
                    type="monotone"
                    dataKey={finCurrencyTab === "usd" ? "profit_usd" : "profit"}
                    stroke="#c9a96e"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#c9a96e", strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
                    name={finCurrencyTab === "usd" ? "profit_usd" : "profit"}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-xs text-gray-400">
              <div className="text-center">
                <i className="ri-funds-line text-2xl text-gray-300 mb-2 block"></i>
                暂无数据
              </div>
            </div>
          )}
        </div>
      )}

      {/* Trend Chart */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <i className="ri-bar-chart-grouped-line text-[#c9a96e] text-lg"></i>
              <h3 className="text-sm font-semibold text-[#1a3a5c]">
                客户趋势
                {filterCountry !== "all" && <span className="ml-1 text-xs text-gray-400">· {filterCountry}</span>}
                {filterAdvisor !== "all" && advisorOptions.find(a => String(a.id) === filterAdvisor) && (
                  <span className="ml-1 text-xs text-gray-400">· {advisorOptions.find(a => String(a.id) === filterAdvisor)?.name}</span>
                )}
              </h3>
            </div>
            <div className="flex items-center gap-1">
              {[
                { value: "all", label: "全部" },
                { value: "90", label: "近90天" },
                { value: "30", label: "近30天" },
              ].map((r) => (
                <button
                  key={r.value}
                  onClick={() => setTimeRange(r.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    timeRange === r.value ? "bg-[#1a3a5c] text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          {trend.length > 0 && (
            <button
              onClick={exportTrendToCSV}
              className="px-3 py-1.5 bg-[#1a3a5c] text-white rounded-lg text-[11px] font-medium hover:bg-[#142d48] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-file-download-line"></i>导出 CSV
            </button>
          )}
        </div>
        {trendLoading ? (
          <div className="h-72 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : chartData.length > 0 ? (
          <div className="w-full h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  axisLine={{ stroke: "#e5e7eb" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                    fontSize: 12,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  }}
                  labelStyle={{ color: "#1a3a5c", fontWeight: 600, marginBottom: 4 }}
                  formatter={(value: any, name: string) => {
                    if (name === "newClients") return [value, "新增客户"];
                    if (name === "activeClients") return [value, "总客户数"];
                    return [value, name];
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                  formatter={(value: string) =>
                    value === "newClients" ? "新增客户" : "总客户数"
                  }
                />
                <Line
                  type="monotone"
                  dataKey="newClients"
                  stroke="#c9a96e"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#c9a96e", strokeWidth: 0 }}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
                />
                <Line
                  type="monotone"
                  dataKey="activeClients"
                  stroke="#1a3a5c"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#1a3a5c", strokeWidth: 0 }}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-72 flex items-center justify-center text-xs text-gray-400">
            暂无数据
          </div>
        )}
      </div>

      {/* Distribution Charts Row 1 */}
      <div className={`grid grid-cols-1 gap-5 ${isCountryFiltered ? "lg:grid-cols-2" : "lg:grid-cols-3"}`}>
        {/* Service Plan Distribution */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-[#1a3a5c] mb-4 flex items-center gap-2">
            <i className="ri-pie-chart-line text-[#c9a96e]"></i>服务方案分布
          </h3>
          {formattedPlanDist.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={formattedPlanDist}
                  dataKey="count"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  onClick={(e: any) => {
                    if (e?.payload?.plan) handlePieClick("service_plan", e.payload.plan);
                  }}
                  className="cursor-pointer"
                >
                  {formattedPlanDist.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={["#c9a96e", "#1a3a5c", "#059669", "#dc2626"][index % 4]} className="cursor-pointer" />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip data={formattedPlanDist} labelKey="label" />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-xs text-gray-400">暂无数据</div>
          )}
        </div>

        {/* Client Source Distribution */}
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-[#1a3a5c] mb-4 flex items-center gap-2">
            <i className="ri-user-received-line text-[#c9a96e]"></i>客户来源分布
          </h3>
          {sourceDist.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={sourceDist.map((d: any) => ({ ...d, name: d.label || d.source }))}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  onClick={(e: any) => {
                    if (e?.payload?.source) handlePieClick("client_source", e.payload.source);
                  }}
                  className="cursor-pointer"
                >
                  {sourceDist.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={["#c9a96e", "#1a3a5c", "#059669", "#dc2626", "#f59e0b", "#0891b2", "#64748b"][index % 7]} className="cursor-pointer" />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip data={sourceDist.map((d: any) => ({ ...d, name: d.label || d.source }))} labelKey="name" />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-xs text-gray-400">暂无数据</div>
          )}
        </div>

        {/* Country Distribution - only show when not filtering by country */}
        {!isCountryFiltered && (
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-semibold text-[#1a3a5c] mb-4 flex items-center gap-2">
              <i className="ri-map-pin-line text-[#c9a96e]"></i>客户国家分布
            </h3>
            {countryDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={countryDist}
                    dataKey="count"
                    nameKey="country"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    onClick={(e: any) => {
                      if (e?.payload?.country) handlePieClick("country", e.payload.country);
                    }}
                    className="cursor-pointer"
                  >
                    {countryDist.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={["#c9a96e", "#1a3a5c", "#059669", "#dc2626", "#f59e0b", "#0891b2"][index % 6]} className="cursor-pointer" />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip data={countryDist} labelKey="country" />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-xs text-gray-400">暂无数据</div>
            )}
          </div>
        )}
      </div>

      {/* Distribution Charts Row 2: Collection + Country Fee */}
      {hasFinanceSubPermission("income") && (
        <div className="space-y-5">
          <div className={`grid grid-cols-1 gap-5 ${isCountryFiltered ? "" : "lg:grid-cols-3"}`}>
            {/* Collection Status Distribution */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="text-sm font-semibold text-[#1a3a5c] mb-4 flex items-center gap-2">
                <i className="ri-pie-chart-line text-[#c9a96e]"></i>客户收款进度
              </h3>
              {feeLoading ? (
                <div className="h-[220px] flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : collectionPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={collectionPieData}
                      dataKey="count"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      onClick={(e: any) => {
                        if (e?.payload?.label === "收款已完成") {
                          navigate("/admin/finance?tab=income&fee_status=completed");
                        } else if (e?.payload?.label === "收款未完成") {
                          navigate("/admin/finance?tab=income&fee_status=incompleted");
                        }
                      }}
                      className="cursor-pointer"
                    >
                      {collectionPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CollectionPieTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[220px] flex items-center justify-center text-xs text-gray-400">暂无数据</div>
              )}
            </div>

            {/* Country Fee Overview - CNY */}
            {!isCountryFiltered && (
              <div className="bg-white rounded-xl shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-[#1a3a5c] flex items-center gap-2">
                    <i className="ri-bar-chart-grouped-line text-[#c9a96e]"></i>各国收款概览（元）
                  </h3>
                  <div className="flex gap-1">
                    {[
                      { key: "receivable", label: "应收" },
                      { key: "received", label: "已收" },
                      { key: "pending", label: "待收" },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setFeeTab(tab.key as any)}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${feeTab === tab.key ? "bg-[#1a3a5c] text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100"}`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
                {(() => {
                  const cnyPieData = countryFeeData.map((d) => ({ country: d.country, name: d.country, value: feeTab === "receivable" ? d.receivable : feeTab === "received" ? d.received : d.pending })).filter((d) => d.value > 0);
                  return feeLoading ? (
                  <div className="h-[260px] flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : cnyPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={cnyPieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={3}
                        onClick={(e: any) => {
                          if (e?.payload?.country) navigate(`/admin/finance?tab=income&country=${e.payload.country}`);
                        }}
                        className="cursor-pointer"
                      >
                        {cnyPieData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={["#c9a96e", "#1a3a5c", "#059669", "#dc2626", "#f59e0b", "#0891b2"][index % 6]} className="cursor-pointer" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip data={cnyPieData} labelKey="name" valueKey="value" currency="¥" />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[260px] flex items-center justify-center text-xs text-gray-400">
                    <div className="text-center"><i className="ri-money-cny-circle-line text-2xl text-gray-300 mb-2 block"></i>暂无数据</div>
                  </div>
                );
                })()}
              </div>
            )}

            {/* Country Fee Overview - USD */}
            {!isCountryFiltered && (
              <div className="bg-white rounded-xl shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-[#1a3a5c] flex items-center gap-2">
                    <i className="ri-bar-chart-grouped-line text-[#c9a96e]"></i>各国收款概览（美元）
                  </h3>
                  <div className="flex gap-1">
                    {[
                      { key: "receivable", label: "应收" },
                      { key: "received", label: "已收" },
                      { key: "pending", label: "待收" },
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setUsdFeeTab(tab.key as any)}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${usdFeeTab === tab.key ? "bg-[#1a3a5c] text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100"}`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
                {(() => {
                  const usdPieData = countryFeeData.map((d) => ({ country: d.country, name: d.country, value: usdFeeTab === "receivable" ? (d as any).usdReceivable || 0 : usdFeeTab === "received" ? (d as any).usdReceived || 0 : (d as any).usdPending || 0 })).filter((d) => d.value > 0);
                  return feeLoading ? (
                  <div className="h-[260px] flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : usdPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={usdPieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={3}
                        onClick={(e: any) => {
                          if (e?.payload?.country) navigate(`/admin/finance?tab=income&country=${e.payload.country}`);
                        }}
                        className="cursor-pointer"
                      >
                        {usdPieData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={["#c9a96e", "#1a3a5c", "#059669", "#dc2626", "#f59e0b", "#0891b2"][index % 6]} className="cursor-pointer" />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip data={usdPieData} labelKey="name" valueKey="value" currency="$" />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[260px] flex items-center justify-center text-xs text-gray-400">
                    <div className="text-center"><i className="ri-money-dollar-circle-line text-2xl text-gray-300 mb-2 block"></i>暂无数据</div>
                  </div>
                );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-sm font-semibold text-[#1a3a5c] mb-4 flex items-center gap-2">
          <i className="ri-flashlight-line text-[#c9a96e]"></i>快捷操作
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <a
            href="/admin/clients"
            className="flex items-center gap-3 p-4 rounded-xl bg-[#1a3a5c]/5 hover:bg-[#1a3a5c]/10 transition-colors cursor-pointer"
          >
            <div className="w-10 h-10 rounded-lg bg-[#1a3a5c] flex items-center justify-center">
              <i className="ri-user-add-line text-white"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-[#1a3a5c]">客户管理</p>
              <p className="text-[11px] text-gray-400">查看和管理客户信息</p>
            </div>
          </a>
          <a
            href="/admin/progress"
            className="flex items-center gap-3 p-4 rounded-xl bg-[#c9a96e]/5 hover:bg-[#c9a96e]/10 transition-colors cursor-pointer"
          >
            <div className="w-10 h-10 rounded-lg bg-[#c9a96e] flex items-center justify-center">
              <i className="ri-list-check-3 text-white"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-[#1a3a5c]">进度管理</p>
              <p className="text-[11px] text-gray-400">更新客户试管流程进度</p>
            </div>
          </a>
          <a
            href="/admin/finance"
            className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 hover:bg-amber-100 transition-colors cursor-pointer"
          >
            <div className="w-10 h-10 rounded-lg bg-amber-600 flex items-center justify-center">
              <i className="ri-money-cny-circle-line text-white"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-[#1a3a5c]">财务管理</p>
              <p className="text-[11px] text-gray-400">查看客户应收已收明细</p>
            </div>
          </a>
          <a
            href="/admin/system"
            className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 hover:bg-emerald-100 transition-colors cursor-pointer"
          >
            <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
              <i className="ri-send-plane-line text-white"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-[#1a3a5c]">推送通知</p>
              <p className="text-[11px] text-gray-400">向客户发送关键节点提醒</p>
            </div>
          </a>
        </div>
      </div>
    </div>
  );
}